import type { E2SiteRow } from "./schedule-misalignment.types"

const DOW = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
]

export const dowLabel = (dow: number): string => DOW[dow] ?? "a weekday"

export const hourLabel = (hour: number): string => {
  const h = ((hour % 24) + 24) % 24
  const period = h < 12 ? "AM" : "PM"
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12} ${period}`
}

// Time-of-day word for a given hour — used so the story phrasing never
// contradicts the hour it names (e.g. a Saturday 11 AM peak must not read
// "Saturday nights around 11 AM").
export const partOfDay = (hour: number): string => {
  const h = ((hour % 24) + 24) % 24
  if (h >= 5 && h <= 11) {
    return "morning"
  }
  if (h >= 12 && h <= 16) {
    return "afternoon"
  }
  if (h >= 17 && h <= 20) {
    return "evening"
  }
  return "overnight"
}

const ENDUSE: Record<string, string> = {
  cooling_kw: "Cooling",
  heating_kw: "Heating",
  fans_kw: "Fans",
  pumps_kw: "Pumps",
  interior_lighting_kw: "Lighting",
  exterior_lighting_kw: "Exterior lighting",
  plugs_kw: "Plug loads",
  wh_kw: "Water heating",
  refrig_kw: "Refrigeration",
  heat_rejection_kw: "Heat rejection",
  heat_recovery_kw: "Heat recovery",
}
export const humanizeEnduse = (enduse: string): string =>
  ENDUSE[enduse] ?? enduse.replace(/_kw$/, "").replace(/_/g, " ")

const FACTOR: Record<string, string> = {
  "short window": "fewer than ~4 days of data",
  "weak occupancy": "the occupied vs. unoccupied signal is faint",
  "flat occupancy": "the occupied vs. unoccupied signal is faint",
}
export const humanizeFactor = (raw: string): string => FACTOR[raw] ?? raw

const topDriver = (site: E2SiteRow) =>
  site.drivers.length
    ? [...site.drivers].sort((a, b) => b.share_pct - a.share_pct)[0]
    : null

export const buildScheduleStory = (site: E2SiteRow): string => {
  const parts: string[] = [
    "After-hours HVAC ran while the building was unoccupied",
  ]
  const d = topDriver(site)
  if (d) {
    parts.push(
      `driven mainly by ${humanizeEnduse(d.enduse).toLowerCase()} (${Math.round(d.share_pct)}%)`
    )
  }
  return `${parts.join(", ")} — equipment conditioning the building while it's empty.`
}

// Humanized MED/LOW factor summary — "fewer than ~4 days of data" etc,
// joined. Shared base for the directional caveat below and the confidence
// prose, so both stay in sync with a single humanization pass.
const caveatFactors = (site: E2SiteRow): string => {
  const factors = site.confidence_factors.map(humanizeFactor)
  return factors.length ? factors.join("; ") : "limited supporting data"
}

// Humanized MED/LOW directional caveat, as a standalone sentence — shared by
// the confidence prose and the SHAP-bars path, so a thin supporting-data
// signal is never hidden just because the block is rendering bars instead of
// a sentence. HIGH confidence has nothing to caveat, so it's null.
export const directionalCaveat = (site: E2SiteRow): string | null =>
  site.confidence === "HIGH"
    ? null
    : `Treat the figure as directional: ${caveatFactors(site)}.`

// Discrete "what guides our analysis" points, for a bulleted render. Each
// entry is one self-contained reason; the last is the HIGH conclusion or the
// MED/LOW directional caveat.
export const buildConfidenceBasisPoints = (
  site: E2SiteRow,
  periodLabel: string
): string[] => {
  const points: string[] = [`Observed across ${periodLabel}`]
  const d = topDriver(site)
  if (d) {
    points.push(
      `${humanizeEnduse(d.enduse).toLowerCase()} dominates (${Math.round(d.share_pct)}%)`
    )
  }
  if (site.confidence === "HIGH") {
    points.push("This is schedule, not weather.")
  } else {
    points.push(`Treat the figure as directional: ${caveatFactors(site)}.`)
  }
  return points
}

// Back-compat single-line form (joins the points) — kept for any prose consumer.
export const buildConfidenceBasis = (
  site: E2SiteRow,
  periodLabel: string
): string => buildConfidenceBasisPoints(site, periodLabel).join(" · ")
